import { useEffect, useRef, useState } from 'react';

import { CheckIcon, CopyIcon } from 'lucide-react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';

import { FieldsetCard } from '@/components/atoms/FieldsetCard/FieldsetCard';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from '@/components/ui/input-group';
import { useIntegrations } from '@/hooks/useIntegrations';
import { getPermissions } from '@/redux/permissions/selectors';
import { MstatViberIntegration } from '@/shared/types/integration';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import { CHANNELS_URL } from '@/util/constants';
import { integrationRulesMstatViber as rules } from '@/util/validation/rules/integration-rules';

interface Props {
  integration: MstatViberIntegration;
}

type SetupForm = { webhook_token: string };

const FORM_ID = 'mstat-viber-setup-form';

/** Cryptographically secure lowercase-alphanumeric token (mirrors the Custom integration). */
const generateToken = (length = 32): string => {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => chars[byte % chars.length]).join('');
};

/** Read-only value with a copy-to-clipboard button. */
const CopyField = ({ label, value }: { label: string; value: string }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(null);

  useEffect(() => () => clearTimeout(timeoutRef.current), []);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error(t('common.error'));
    }
  };

  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-sm leading-5 font-medium">{label}</label>
      <InputGroup size="md" className="w-full bg-background">
        <InputGroupInput value={value} readOnly className="text-sm text-foreground-muted" />
        <InputGroupAddon align="inline-end">
          <InputGroupButton
            type="button"
            size="icon-sm"
            onClick={handleCopy}
            aria-label={t('common.copy')}
            className="min-w-0"
          >
            {copied ? (
              <CheckIcon className="text-foreground-muted" size={16} />
            ) : (
              <CopyIcon className="text-foreground-muted" size={16} />
            )}
          </InputGroupButton>
        </InputGroupAddon>
      </InputGroup>
    </div>
  );
};

/**
 * "Configure in MSTAT" card: the three inbound endpoint URLs to register in the
 * MSTAT console (copy fields) and the webhook bearer token MSTAT must send with
 * every inbound request (generate + copy, editable, saved to the integration).
 */
const MstatSetupCard = ({ integration }: Props) => {
  const { t } = useTranslation();
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'integrations', actions.WRITE)
  );
  const { updateIntegration, updateStatus } = useIntegrations<MstatViberIntegration>(
    integration?.desk_id,
    integration?.integration_id
  );

  const [copiedToken, setCopiedToken] = useState(false);
  const tokenTimeoutRef = useRef<ReturnType<typeof setTimeout>>(null);

  useEffect(() => () => clearTimeout(tokenTimeoutRef.current), []);

  const form = useForm<SetupForm>({
    mode: 'onSubmit',
    defaultValues: { webhook_token: integration.config?.webhook_token ?? '' },
  });

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { isDirty },
  } = form;

  const baseUrl = `${CHANNELS_URL}/v1/mstat-viber/${integration.integration_id}`;
  const webhookToken = useWatch({ control, name: 'webhook_token' });

  const handleGenerate = () => {
    setValue('webhook_token', generateToken(32), { shouldValidate: true, shouldDirty: true });
  };

  const handleCopyToken = async () => {
    try {
      await navigator.clipboard.writeText(webhookToken);
      setCopiedToken(true);
      clearTimeout(tokenTimeoutRef.current);
      tokenTimeoutRef.current = setTimeout(() => setCopiedToken(false), 2500);
    } catch {
      toast.error(t('common.error'));
    }
  };

  const onSubmit = (values: SetupForm) => {
    updateIntegration(
      { ...integration, config: { ...integration.config, webhook_token: values.webhook_token } },
      { onSuccess: () => reset(values) }
    );
  };

  return (
    <div className="w-full">
      <p className="mb-2 px-1.5 text-sm leading-none font-medium text-foreground-muted">
        {t('integrations.mstat_viber_setup.title')}
      </p>
      <Form {...form}>
        <form id={FORM_ID} onSubmit={handleSubmit(onSubmit)}>
          <FieldsetCard
            isDirty={isDirty}
            isLoading={updateStatus === 'pending'}
            disabled={!isDirty || !canWrite}
            saveButtonLabel={t('common.save')}
            formId={FORM_ID}
          >
            <div className="flex flex-col gap-6">
              <p className="text-sm leading-5 text-foreground-muted">
                {t('integrations.mstat_viber_setup.subtitle')}
              </p>

              <div className="flex flex-col gap-4">
                <CopyField
                  label={t('integrations.mstat_viber_setup.endpoints.incoming_message')}
                  value={`${baseUrl}/incoming-message`}
                />
                <CopyField
                  label={t('integrations.mstat_viber_setup.endpoints.interaction')}
                  value={`${baseUrl}/interaction`}
                />
                <CopyField
                  label={t('integrations.mstat_viber_setup.endpoints.delivery_report')}
                  value={`${baseUrl}/delivery-report`}
                />
              </div>

              <FormField
                control={control}
                name="webhook_token"
                rules={{
                  required: t('common.required_field'),
                  minLength: {
                    value: rules.webhookToken.minLength,
                    message: t('validation.min_length', { 0: rules.webhookToken.minLength }),
                  },
                  maxLength: {
                    value: rules.webhookToken.maxLength,
                    message: t('validation.max_length', { 0: rules.webhookToken.maxLength }),
                  },
                }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('integrations.mstat_viber_setup.webhook_token.title')}</FormLabel>
                    <div className="flex gap-2">
                      <FormControl>
                        <InputGroup size="md" className="flex-1 bg-background">
                          <InputGroupInput
                            placeholder={t(
                              'integrations.mstat_viber_setup.webhook_token.placeholder'
                            )}
                            disabled={!canWrite}
                            {...field}
                            value={field.value ?? ''}
                          />
                          <InputGroupAddon align="inline-end">
                            <InputGroupButton
                              type="button"
                              size="icon-sm"
                              onClick={handleCopyToken}
                              disabled={!webhookToken}
                              aria-label={t('common.copy')}
                              className="min-w-0"
                            >
                              {copiedToken ? (
                                <CheckIcon className="text-foreground-muted" size={16} />
                              ) : (
                                <CopyIcon className="text-foreground-muted" size={16} />
                              )}
                            </InputGroupButton>
                          </InputGroupAddon>
                        </InputGroup>
                      </FormControl>
                      <Button
                        type="button"
                        variant="outline-neutral"
                        onClick={handleGenerate}
                        disabled={!canWrite}
                        className="shrink-0"
                      >
                        {t('integrations.mstat_viber_setup.webhook_token.generate')}
                      </Button>
                    </div>
                    <FormDescription>
                      {t('integrations.mstat_viber_setup.webhook_token.subtitle')}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </FieldsetCard>
        </form>
      </Form>
    </div>
  );
};

export default MstatSetupCard;
